import { useEffect, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Stack,
  Autocomplete,
  Box,
  Container,
  Grid,
  Button,
  InputAdornment,
} from "@mui/material";

import { useAppSelector, useAppDispatch } from "../redux/hooks";
import {
  getApp,
  country,
  AppCountry,
  login,
  backdrop,
  message,
  logout,
} from "../redux/app.slice";

import Logo from "../assets/img/logo.svg";

import { NavigationComponent } from "./navigation";

import { TokenAuth } from "../modules/token";

import { countryService, loginService } from "../modules/service";

export const LoginComponent = () => {
  const navigate = useNavigate();
  const app = useAppSelector(getApp);
  const dispatch = useAppDispatch();

  const [Phone, SetPhone] = useState("");
  const [DefaultCountry, SetDefaultCountry] = useState<AppCountry | null>();

  const [Student, SetStudent] = useState<{ country: string; phone: string }>({
    country: "",
    phone: "",
  });

  const [Error, SetError] = useState<{ country: boolean; phone: boolean }>({
    country: false,
    phone: false,
  });

  useEffect(() => {
    if (app.countries) {
      if (app.countries.length === 0) {
        dispatch(backdrop(true));
        countryService()
          .then((response) => {
            dispatch(country(response.data));
          })
          .catch((error) => console.log(error))
          .finally(() => dispatch(backdrop(false)));
      }
    }
  }, [app.countries, dispatch]);

  useEffect(() => {
    if (app.student && app.token) {
      dispatch(backdrop(true));
      TokenAuth(app.student, app.token)
        .then((response) => {
          navigate("/dashboard");
        })
        .catch((error) => {
          dispatch(logout());
        })
        .finally(() => dispatch(backdrop(false)));
    }
  }, [navigate, app.student, app.token, dispatch]);

  useEffect(() => {
    if (app.student) {
      SetStudent({ country: app.student.country, phone: app.student.phone });
      const country = app.countries.find(
        ({ id }) => id === app.student!.country
      );
      SetDefaultCountry(country ? country : null);
    }
  }, [app.student, app.countries]);

  const HandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    SetStudent({ ...Student, [event.target.name]: event.target.value });
  };

  const HandleValidate = () => {
    let error: { country: boolean; phone: boolean } = {
      country: true,
      phone: true,
    };

    if (Student.country === "") error.country = true;
    else error.country = false;

    if (Student.phone === "") error.phone = true;
    else error.phone = false;

    SetError(error);

    return Object.values(error).every((value) => value === false);
  };

  const HandleSubmit = () => {
    if (app.student && app.student.phone !== Student.phone) {
      dispatch(
        message({
          open: true,
          message: "Sorry, you can only use one device for login",
          severity: "error",
        })
      );
    } else {
      if (HandleValidate()) {
        dispatch(backdrop(true));
        const student_form_data = new FormData();
        student_form_data.append("country", Student.country);
        student_form_data.append("phone", Student.phone);
        loginService(student_form_data)
          .then((response) => {
            dispatch(login(response.data));
            navigate(
              `/auth/${response.data.id}/${response.data.secret}/${Student.country}/${Student.phone}`
            );
          })
          .catch((error) => {
            dispatch(
              message({
                open: true,
                message:
                  error.response?.data?.message ||
                  "Sorry, you are not registered. Please register first.",
                severity: "error",
              })
            );
          })
          .finally(() => dispatch(backdrop(false)));
      }
    }
  };

  return (
    <>
      <NavigationComponent />
      <Container maxWidth="sm" style={{ margin: "2em auto" }}>
        <Stack spacing={2}>
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ marginBottom: "1em" }}
          >
            <img
              src={Logo}
              width={100}
              alt="London School of Commerce Group of Colleges Logo"
            />
          </Grid>

          <Autocomplete
            options={app.countries}
            onChange={(event, value) => {
              if (value) {
                SetStudent({ ...Student, country: value.id });
                SetPhone(`+${value.phone}`);
              }
            }}
            autoHighlight
            getOptionLabel={(option) => `${option.name} (+${option.phone})`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name} (+{option.phone})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                error={Error.country}
                {...params}
                label="Country"
                name="country"
                helperText="Select your country"
              />
            )}
          />
          <TextField
            name="phone"
            error={Error.phone}
            label="Phone"
            value={Student.phone}
            onChange={HandleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{Phone}</InputAdornment>
              ),
            }}
            helperText="Enter your phone number"
            fullWidth
          />
          <Button
            variant="contained"
            disableElevation
            size="large"
            onClick={HandleSubmit}
          >
            Login
          </Button>
        </Stack>
      </Container>
    </>
  );
};
